/* eslint-disable no-unused-vars */
import {useORingCalculatorStore} from '~/store/oring-calculator'
import useI18nExtended from '~/composables/usei18nExtended'
import type {ToleranceMinMaxData} from './useToleranceDataManager'

export function useToleranceFormatter() {
  const i18n = useI18nExtended()
  const store = useORingCalculatorStore()
  const {oringDesignCockpitCurrentHousing, oringCalculatorImperial} = storeToRefs(store)

  // Chamfer angle reactive value
  const chamferAngleValue = ref<number | null>(null)

  // Get unit label based on imperial setting
  const unitLabel = computed(() => {
    return oringCalculatorImperial.value ? i18n.tv('inch', 'unitsOfMeasure') : i18n.tv('mm', 'unitsOfMeasure')
  })

  // Helper function to format with smart decimals (3 if needed, otherwise 2)
  function formatValue(value: number): string {
    const rounded3 = Number(value.toFixed(3))
    const rounded2 = Number(value.toFixed(2))
    const formatted = rounded3 !== rounded2 ? value.toFixed(3) : value.toFixed(2)
    return formatted.replace('.', ',')
  }

  // Get parameter min/max text for display below input field
  function getParameterMinMaxText(
    key: string,
    toleranceMinMaxData: Record<string, ToleranceMinMaxData>
  ): string {
    const toleranceData = toleranceMinMaxData[key]
    const unit = unitLabel.value
    if (toleranceData) {
      const min = formatValue(toleranceData.parameterMin)
      const max = formatValue(toleranceData.parameterMax)
      return `Min.: ${min} ${unit} / Max.: ${max} ${unit}`
    }
    return `Min.: 0,00 ${unit} / Max.: 0,00 ${unit}`
  }

  // Get tolerance min/max text for display below tolerance dropdown
  function getToleranceMinMaxText(
    key: string,
    toleranceMinMaxData: Record<string, ToleranceMinMaxData>
  ): string {
    const toleranceData = toleranceMinMaxData[key]
    const toleranceLabel = i18n.tv('Frontend.Report.Tolerance', 'ORingConfiguratorImport')
    const unit = unitLabel.value
    if (toleranceData) {
      // Calculate the tolerance range (assuming it's symmetrical ±)
      const toleranceRange = Math.abs(toleranceData.toleranceMax - toleranceData.toleranceMin) / 2
      const tolerance = formatValue(toleranceRange)
      return `${toleranceLabel} ± ${tolerance} ${unit}`
    }
    return `${toleranceLabel} ± 0,00 ${unit}`
  }

  // Initialize chamfer angle value from housing
  function initializeChamferAngleValue() {
    if (oringDesignCockpitCurrentHousing.value?.chamferAngle?.parameterValue !== undefined) {
      chamferAngleValue.value = oringDesignCockpitCurrentHousing.value.chamferAngle.parameterValue
    } else if (oringDesignCockpitCurrentHousing.value) {
      // Set default value of 15 if chamferAngle doesn't exist or has no value
      chamferAngleValue.value = 15
      // Also initialize the housing object's chamferAngle property
      if (!oringDesignCockpitCurrentHousing.value.chamferAngle) {
        oringDesignCockpitCurrentHousing.value.chamferAngle = {
          toleranceClassId: 1,
          toleranceClassName: null,
          parameterValue: 15,
          val: 15,
          roundedVal: 15,
          toleranceMin: 0,
          toleranceMax: 0,
          parameterMin: 0,
          parameterMax: 0,
          isEqual: false
        }
      } else if (oringDesignCockpitCurrentHousing.value.chamferAngle.parameterValue === 0) {
        // Update existing chamferAngle with default value
        oringDesignCockpitCurrentHousing.value.chamferAngle.parameterValue = 15
        oringDesignCockpitCurrentHousing.value.chamferAngle.val = 15
        oringDesignCockpitCurrentHousing.value.chamferAngle.roundedVal = 15
      }
    }
  }

  // Handle chamfer angle input updates
  function handleChamferAngleUpdate() {
    if (!oringDesignCockpitCurrentHousing.value) {
      return
    }

    // Update the housing object with the new chamfer angle value
    if (!oringDesignCockpitCurrentHousing.value.chamferAngle) {
      oringDesignCockpitCurrentHousing.value.chamferAngle = {
        toleranceClassId: 1,
        toleranceClassName: null,
        parameterValue: 0,
        val: 0,
        roundedVal: 0,
        toleranceMin: 0,
        toleranceMax: 0,
        parameterMin: 0,
        parameterMax: 0,
        isEqual: false
      }
    }

    oringDesignCockpitCurrentHousing.value.chamferAngle.parameterValue = chamferAngleValue.value || 0
    oringDesignCockpitCurrentHousing.value.chamferAngle.val = chamferAngleValue.value || 0

    // No API call for chamfer angle as it has no tolerance data
  }

  return {
    chamferAngleValue,
    unitLabel,
    formatValue,
    getParameterMinMaxText,
    getToleranceMinMaxText,
    initializeChamferAngleValue,
    handleChamferAngleUpdate
  }
}
